/**
 * Post-purchase welcome page: the WordPress / FunnelKit thank-you page rebuilt
 * in the app, mirroring its copy and the order of its elements exactly (Ben's
 * requirement — the sequence is deliberate and emotional, not transactional):
 *
 *   photo → "Best Decision Ever" → congratulations copy → quick highlights →
 *   "Your fellow musicians love it here" → "Your Next Step" + survey →
 *   order confirmation.
 *
 * Single column, designed for mobile first; desktop just centres the column.
 * Piano beginners and intermediates go straight into Jazz Piano First Steps
 * after the survey, as the old page did.
 */
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Linking, Platform, ScrollView } from 'react-native';
import { Button, SizableText, XStack, YStack, useMedia } from 'tamagui';
import { FindDashboardSurveyModal } from '@/components/courses/FindDashboardSurveyModal';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText, CardTitle, PageTitle, SectionTitle } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { readHomeSurveyPrefillFromMetadata } from '@/lib/dashboardHomeSurvey';
import { logger } from '@/lib/logger';
import { formatMoney, formatWelcomeDate, periodSuffix, type WelcomePayload } from '@/lib/purchaseWelcome';
import { resolveUserHomeUrl } from '@/lib/userHomeUrl';
import { ensureVwoSmartCode, trackVwoEvent } from '@/lib/vwo';
import { firstStepsCourseHref, surveyOpensFirstSteps } from '@/lib/welcomeFirstSteps';
import { gaService } from '@/services/ga.service';

const PAGE_MAX_WIDTH = 680;

/** Same assets the WordPress thank-you page used (the photo in the full-size version). */
const COMMUNITY_PHOTO = 'https://www.openstudiojazz.com/wp-content/uploads/2026/05/peter-adam-cancel1.jpeg';
const REVIEWS_IMAGE = 'https://www.openstudiojazz.com/wp-content/uploads/2026/04/image5.png';
const SUPPORT_IMAGE = 'https://www.openstudiojazz.com/wp-content/uploads/2026/04/image4.png';
const PRO_HUDDLE_URL = 'https://calendar.app.google/6EYEx1dLMvmz5w8Z9';

const VWO_TRIAL_EVENT = 'trialStarted';
const VWO_PLAN_PROPERTY = 'plan';

type Props = { payload: WelcomePayload };

export function WelcomeScreen({ payload }: Props) {
  const router = useRouter();
  const media = useMedia();
  const { user, loading: authLoading, preferenceMetadata, membershipTier } = useAuth();

  const hasInstrument = !!readHomeSurveyPrefillFromMetadata(preferenceMetadata).instrument;
  const homeHref = useMemo(() => {
    const home = resolveUserHomeUrl(preferenceMetadata ?? undefined, membershipTier, false);
    return home.startsWith('/courses/') ? home : '/courses';
  }, [preferenceMetadata, membershipTier]);

  // Conversion tracking (web only, once per order per browser session) — the
  // events the WordPress thank-you page used to fire.
  const trackedOrderRef = useRef<number | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || trackedOrderRef.current === payload.order) return;
    trackedOrderRef.current = payload.order;

    const key = `osj_welcome_tracked_${payload.order}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch {
      // Private mode / blocked storage: fire anyway, the ref still dedupes re-renders.
    }

    try {
      gaService.trackPurchase({
        transactionId: String(payload.order),
        value: payload.total,
        currency: payload.currency,
        items: payload.items.map((item) => ({
          id: String(item.id),
          name: item.name,
          price: item.qty ? item.total / item.qty : item.total,
          quantity: item.qty,
          category: item.kind,
        })),
      });
      if (payload.vwo.length) {
        ensureVwoSmartCode();
        for (const plan of payload.vwo) {
          trackVwoEvent(VWO_TRIAL_EVENT, { [VWO_PLAN_PROPERTY]: plan });
        }
      }
    } catch (err) {
      logger.warn('[welcome] conversion tracking failed', err);
    }
  }, [payload]);

  // Google Ads / Podscribe conversions: `os_order_complete`, one per paid
  // order of the purchase (see lib/purchaseWelcome.ts). Waits for the session
  // so the buyer's email travels with the event (enhanced conversions); once
  // auth has settled without a user it fires without the email rather than
  // never.
  const conversionsOrderRef = useRef<number | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || conversionsOrderRef.current === payload.order) return;
    const email = user?.email ?? '';
    if (!email && authLoading) return;
    conversionsOrderRef.current = payload.order;

    try {
      for (const conversion of payload.conversions) {
        gaService.trackOrderComplete(conversion, email);
      }
    } catch (err) {
      logger.warn('[welcome] ads conversion tracking failed', err);
    }
  }, [payload, user?.email, authLoading]);

  // The survey saves instrument + level into the auth context before calling
  // onSuccess, so by the time this effect runs homeHref and the First Steps
  // audience check already reflect the answers. Piano beginners/intermediates
  // go straight into Jazz Piano First Steps; everyone else to their dashboard.
  const [surveyDone, setSurveyDone] = useState(false);
  useEffect(() => {
    if (!surveyDone) return;
    const next = surveyOpensFirstSteps(preferenceMetadata) ? firstStepsCourseHref() : homeHref;
    router.replace(next as never);
  }, [surveyDone, preferenceMetadata, homeHref, router]);

  const goToCourses = useCallback(() => router.replace('/courses' as never), [router]);
  const goHome = useCallback(() => router.replace(homeHref as never), [router, homeHref]);

  const wide = media.gtSm;

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingBottom: 64 }} showsVerticalScrollIndicator={false}>
        <Container maxWidth={PAGE_MAX_WIDTH} centered px="$5" pt={wide ? '$8' : '$5'}>
          <YStack gap={wide ? '$6' : '$5'} w="100%">
            <CommunityPhoto />

            <Intro payload={payload} />

            <Highlights payload={payload} />

            <SocialProof />

            <NextStep
              payload={payload}
              hasInstrument={hasInstrument}
              redirecting={surveyDone}
              onSurveyDone={() => setSurveyDone(true)}
              onSkip={goToCourses}
              onGoHome={goHome}
              onBrowse={goToCourses}
            />

            <OrderConfirmation payload={payload} email={user?.email ?? ''} />
          </YStack>
        </Container>
      </ScrollView>
    </YStack>
  );
}

/* ------------------------------------------------------------------------ */

function CommunityPhoto() {
  return (
    <YStack w="100%" aspectRatio={3 / 2} br="$6" overflow="hidden" bg="#E8E4DF">
      <Image
        source={{ uri: COMMUNITY_PHOTO }}
        style={{ width: '100%', height: '100%' }}
        contentFit="cover"
        accessibilityLabel="Peter Martin and Adam Maness at the piano"
      />
    </YStack>
  );
}

function Intro({ payload }: { payload: WelcomePayload }) {
  const media = useMedia();
  return (
    <YStack gap="$4">
      <PageTitle color={C.text} fontSize={media.gtSm ? 38 : 30} lineHeight={media.gtSm ? 44 : 36}>
        {payload.first ? `🙌 Best Decision Ever, ${payload.first}!` : '🙌 Best Decision Ever!'}
      </PageTitle>

      <Paragraph>
        {"Congratulations! You're the type of player who takes action to make your musical dreams come to life."}
      </Paragraph>

      <Paragraph>
        {"You're now part of "}
        <Strong>the #1 jazz community,</Strong>
        {' which gives you everything you need to unlock your inner musician.'}
      </Paragraph>

      <Paragraph>Here are a few quick highlights…</Paragraph>
    </YStack>
  );
}

/* ------------------------------------------------------------------------ */

function Highlights({ payload }: { payload: WelcomePayload }) {
  const isPro = payload.tier === 'pro';
  const trialEnd = payload.trial && payload.trialEnd ? formatWelcomeDate(payload.trialEnd) : '';

  let access: ReactNode;
  if (isPro) {
    access = "You've unlocked access to 20+ live classes each week!";
  } else if (trialEnd) {
    access = (
      <>
        {'Your '}
        <Strong>free trial</Strong>
        {` gives you unlimited access through ${trialEnd} (don't worry – we'll send you an email reminder before it ends 🌴).`}
      </>
    );
  } else {
    access = 'Your subscription gives you unlimited access to the entire Open Studio course library.';
  }

  return (
    <YStack gap="$3.5">
      <HighlightRow icon={payload.trial ? 'calendar-outline' : 'infinite-outline'}>
        <Paragraph f={1}>{access}</Paragraph>
      </HighlightRow>

      <HighlightRow icon="shield-checkmark-outline">
        <Paragraph f={1}>
          {"We've made this completely "}
          <Strong>risk-free</Strong>
          {' by giving you our 30-day, 100% money-back guarantee'}
          {payload.trial ? ' if you choose to stay on after your trial.' : '.'}
        </Paragraph>
      </HighlightRow>

      <HighlightRow icon="chatbubble-ellipses-outline">
        <XStack f={1} ai="center" gap="$3" flexWrap="wrap">
          <Paragraph f={1} minWidth={200}>
            <Strong>{"We're here for you"}</Strong>
            {'. Simply click the help button at the bottom of any page.'}
          </Paragraph>
          <Image
            source={require('@/assets/images/help-beacon.png')}
            style={{ width: 44, height: 44 }}
            contentFit="contain"
            accessibilityLabel="The help button"
          />
        </XStack>
      </HighlightRow>
    </YStack>
  );
}

function HighlightRow({ icon, children }: { icon: keyof typeof Ionicons.glyphMap; children: ReactNode }) {
  return (
    <XStack ai="flex-start" gap="$3">
      <YStack width={40} height={40} br={20} bg="rgba(228, 87, 46, 0.10)" ai="center" jc="center" flexShrink={0}>
        <Ionicons name={icon} size={20} color={C.accent} />
      </YStack>
      {children}
    </XStack>
  );
}

/* ------------------------------------------------------------------------ */

function SocialProof() {
  const media = useMedia();
  const sideBySide = media.gtSm;
  return (
    <YStack gap="$3">
      <Paragraph>
        {'Your fellow musicians '}
        <Strong>love</Strong>
        {' it here…'}
      </Paragraph>
      <Card p="$3" flexDirection={sideBySide ? 'row' : 'column'} ai="center" jc="center" gap="$3">
        <Image
          source={{ uri: REVIEWS_IMAGE }}
          style={{ width: '100%', maxWidth: 300, aspectRatio: 450 / 288 }}
          contentFit="contain"
          accessibilityLabel="Customer reviews: 4.8 rating, 2,803 reviews"
        />
        <Image
          source={{ uri: SUPPORT_IMAGE }}
          style={{ width: '100%', maxWidth: 300, aspectRatio: 450 / 288 }}
          contentFit="contain"
          accessibilityLabel='Customer support rated "Great", 94%'
        />
      </Card>
    </YStack>
  );
}

/* ------------------------------------------------------------------------ */

function NextStep({
  payload,
  hasInstrument,
  redirecting,
  onSurveyDone,
  onSkip,
  onGoHome,
  onBrowse,
}: {
  payload: WelcomePayload;
  hasInstrument: boolean;
  redirecting: boolean;
  onSurveyDone: () => void;
  onSkip: () => void;
  onGoHome: () => void;
  onBrowse: () => void;
}) {
  const isPro = payload.tier === 'pro';

  let body: ReactNode;
  if (redirecting) {
    body = (
      <YStack gap="$2">
        <CardTitle color={C.text}>Setting up your dashboard</CardTitle>
        <Paragraph>One moment while we line up the courses that fit you best.</Paragraph>
      </YStack>
    );
  } else if (!hasInstrument) {
    body = (
      <FindDashboardSurveyModal
        embedded
        tone="onboarding"
        open
        welcomeHeader={{
          title: "Let's find the best courses for you",
          subtitle: "First, what's your primary instrument?",
        }}
        onOpenChange={() => {}}
        onSuccess={onSurveyDone}
        onSkip={onSkip}
      />
    );
  } else {
    body = (
      <YStack gap="$3">
        <CardTitle color={C.text}>Your dashboard is ready</CardTitle>
        <Paragraph>
          {"We've set up a home dashboard around your instrument and level. Pick up right where it makes sense for you."}
        </Paragraph>
        <PrimaryButton onPress={onGoHome}>Go to my dashboard</PrimaryButton>
        <Button size="$3" chromeless color={C.muted} fontWeight="600" onPress={onBrowse} alignSelf="center">
          Browse all courses instead
        </Button>
      </YStack>
    );
  }

  return (
    <YStack gap="$4" pt="$2">
      <SectionTitle color={C.text} textAlign="center" fontSize={28} lineHeight={34}>
        Your Next Step …
      </SectionTitle>

      {isPro ? (
        <Paragraph>
          {"You can start anywhere you'd like; we recommend this easy first step… "}
          <SizableText
            fontFamily="$body"
            fontSize="$4"
            lineHeight={24}
            color={C.linkAccent}
            fontWeight="700"
            textDecorationLine="underline"
            onPress={() => void Linking.openURL(PRO_HUDDLE_URL)}
          >
            Schedule your OS Pro Huddle 1-on-1 call with Bob DeBoo
          </SizableText>
          {' to get your personalized roadmap.'}
        </Paragraph>
      ) : (
        <Paragraph>
          {"You can start anywhere you'd like, but we recommend answering the 2 questions below. This helps us guide " +
            'you to the best courses for you…'}
        </Paragraph>
      )}

      <Card>{body}</Card>
    </YStack>
  );
}

/* ------------------------------------------------------------------------ */

function OrderConfirmation({ payload, email }: { payload: WelcomePayload; email: string }) {
  const recurring =
    payload.recurring > 0 ? `${formatMoney(payload.recurring, payload.currency)}${periodSuffix(payload.period, payload.interval)}` : '';
  const nextDate = payload.trial && payload.trialEnd ? payload.trialEnd : payload.nextPayment;

  return (
    <Card gap="$3" mt="$2">
      <CaptionText color={C.muted} fontWeight="700" letterSpacing={1.1} textTransform="uppercase">
        {`Order ID #${payload.number}`}
      </CaptionText>
      <CardTitle color={C.text}>
        {payload.first ? `Thank You For Your Order ${payload.first}!` : 'Thank You For Your Order!'}
      </CardTitle>
      <Paragraph>
        {`We are pleased to confirm your order no. #${payload.number}`}
        {email ? (
          <>
            {' and an email has been sent to '}
            <Strong>{email}</Strong>
          </>
        ) : null}
      </Paragraph>

      <YStack h={1} bg="rgba(0,0,0,0.06)" />

      <YStack gap="$2">
        {payload.items.map((item) => (
          <XStack key={`${item.id}-${item.name}`} jc="space-between" ai="flex-start" gap="$3">
            <BodyText color={C.text} f={1}>
              {item.qty > 1 ? `${item.name} × ${item.qty}` : item.name}
            </BodyText>
            <BodyText color={C.text} fontWeight="600">
              {formatMoney(item.total, payload.currency)}
            </BodyText>
          </XStack>
        ))}
      </YStack>

      <YStack h={1} bg="rgba(0,0,0,0.06)" />

      <XStack jc="space-between" ai="center">
        <BodyText color={C.text} fontWeight="700">
          Total
        </BodyText>
        <BodyText color={C.text} fontWeight="700">
          {formatMoney(payload.total, payload.currency)}
        </BodyText>
      </XStack>
      {!!recurring && (
        <CaptionText color={C.muted}>
          {nextDate ? `Then ${recurring} starting ${formatWelcomeDate(nextDate)}.` : `Then ${recurring}.`}
        </CaptionText>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------ */

function Paragraph({ children, ...rest }: { children: ReactNode } & Record<string, unknown>) {
  return (
    <BodyText color={C.text} fontSize="$4" lineHeight={24} {...rest}>
      {children}
    </BodyText>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return (
    <SizableText fontFamily="$body" fontSize="$4" lineHeight={24} fontWeight="700" color={C.text}>
      {children}
    </SizableText>
  );
}

function Card({ children, ...rest }: { children: ReactNode } & Record<string, unknown>) {
  return (
    <YStack
      bg={C.cardBg}
      br="$6"
      p="$5"
      borderWidth={1}
      borderColor={C.border}
      gap="$3"
      w="100%"
      style={{
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.06,
        shadowRadius: 24,
        elevation: 3,
      }}
      {...rest}
    >
      {children}
    </YStack>
  );
}

function PrimaryButton({ children, onPress }: { children: ReactNode; onPress: () => void }) {
  return (
    <Button
      onPress={onPress}
      bg={C.accent}
      color={C.cardBg}
      fontWeight="700"
      fontSize="$4"
      br="$4"
      h={52}
      w="100%"
      pressStyle={{ opacity: 0.88 }}
      hoverStyle={{ opacity: 0.92 }}
    >
      {children}
    </Button>
  );
}
